import type { Application } from '@/types';

export function isApplicationArchived(
  app: Pick<Application, 'archived'>,
): boolean {
  return app.archived === true;
}

/** Client-side guard when BE ignores `?archived=` (pre-deploy or stale API). */
export function filterApplicationQueue(
  applications: Application[],
  archived: boolean,
): Application[] {
  return applications.filter((a) =>
    archived ? a.archived === true : a.archived !== true,
  );
}

export function toBulkArchivePayload(
  apps: Pick<Application, 'id' | 'positionId'>[],
): Array<{ applicationId: string; positionId: string }> {
  return apps.map((a) => ({ applicationId: a.id, positionId: a.positionId }));
}

export function removeApplicationsById(
  apps: Application[],
  ids: Set<string>,
): Application[] {
  if (ids.size === 0) return apps;
  return apps.filter((a) => !ids.has(a.id));
}

/** Active queue + archive — for analytics; delete (future) would omit rows entirely. */
/**
 * Prefer a fully loaded list total when the server count is stale/inflated
 * (e.g. active-count cached before archive on another page).
 */
export function reconcileQueueTabCount(
  apiCount: number | null | undefined,
  loadedCount: number,
  listFullyLoaded: boolean,
): number | null | undefined {
  if (apiCount == null) return apiCount;
  if (!listFullyLoaded || loadedCount <= 0) return apiCount;
  if (apiCount > loadedCount) return loadedCount;
  return apiCount;
}

export function mergeApplicationsForAnalytics(
  active: Application[],
  archived: Application[],
): Application[] {
  if (archived.length === 0) return active;
  if (active.length === 0) return archived;
  const byId = new Map<string, Application>();
  for (const app of active) byId.set(app.id, app);
  for (const app of archived) {
    if (!byId.has(app.id)) byId.set(app.id, app);
  }
  return [...byId.values()];
}
